import React, { useEffect, useState } from "react";
import { Box, Flex, Switch } from "theme-ui";
import axios from "axios";

import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { themevals } from "theme/themevals";
import { setClientSurchargeWaived } from "api/FuelSurcharge";

/**
 * Client record > Overview: pricing overrides for THIS client.
 *  - Waive the fuel surcharge. The provider's cost still rides on the line
 *    (so the PO stays right); the client sees "Fuel surcharge (waived) $0.00".
 *    New quotes only; existing quotes change via the quote's own switch.
 *  - Minimum quantity for an Instant Estimate, overriding the company
 *    minimum (empty = use the company minimum).
 * Staff only (the routes are company-only; on a 403 the card hides).
 */
const label: React.CSSProperties = { fontFamily: "Poppins", fontSize: 13, fontWeight: 600, color: "#000" };
const sub: React.CSSProperties = { fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey };

const ClientPricingOverrides: React.FC<{ clientId: string }> = ({ clientId }) => {
  const toast = useToast();
  const [loaded, setLoaded] = useState(false);
  const [waived, setWaived] = useState(false);
  const [minQty, setMinQty] = useState<string>("");
  const [savedMin, setSavedMin] = useState<string>("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`/fuel-surcharge/clients/${clientId}`, { excludeInterceptor: true } as any).then((r) => r?.data).catch(() => null),
      axios.get(`/estimates/settings/clients/${clientId}`, { excludeInterceptor: true } as any).then((r) => r?.data).catch(() => null),
    ]).then(([fuel, min]) => {
      if (cancelled) return;
      if (!fuel && !min) return; // not staff, or routes unavailable: hide
      setWaived(!!fuel?.waived);
      const v = min?.minQuantityYds ? String(min.minQuantityYds) : "";
      setMinQty(v);
      setSavedMin(v);
      setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [clientId]);

  if (!loaded) return null;

  const toggleWaiver = async (on: boolean) => {
    setBusy(true);
    const r: any = await setClientSurchargeWaived(clientId, on);
    setBusy(false);
    if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
    setWaived(on);
    toast.add(on ? "Fuel surcharge waived for this client on new quotes." : "Fuel surcharge applies to this client again.", "var(--theme-ui-colors-success)");
  };

  const saveMin = async () => {
    setBusy(true);
    try {
      const n = minQty.trim() === "" ? null : Number(minQty);
      const r = await axios.put(`/estimates/settings/clients/${clientId}`, { minQuantityYds: n });
      const v = r?.data?.minQuantityYds ? String(r.data.minQuantityYds) : "";
      setMinQty(v);
      setSavedMin(v);
      toast.add(v ? `Minimum for this client set to ${v} yd.` : "This client uses the company minimum.", "var(--theme-ui-colors-success)");
    } catch (e: any) {
      toast.add(e?.response?.data?.message ?? "Saving the minimum failed.", "var(--theme-ui-colors-danger)");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ border: "1px solid", borderColor: themevals.colors.inputOutline, borderRadius: "6px", padding: "16px 18px", marginTop: "24px", background: "#fff" }}>
      <div style={{ fontFamily: "Rubik", fontWeight: 600, fontSize: 15, marginBottom: 12 }}>Pricing for this client</div>
      <Flex sx={{ alignItems: "center", gap: "12px", marginBottom: "14px" }}>
        <Box sx={{ width: "44px" }}>
          <Switch checked={waived} disabled={busy} onChange={(e: any) => toggleWaiver(e.target.checked)} />
        </Box>
        <Box>
          <div style={label}>Waive the fuel surcharge</div>
          <div style={sub}>New quotes show it as waived ($0.00). Existing quotes: use the switch on each quote.</div>
        </Box>
      </Flex>
      <Flex sx={{ alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <div style={{ ...label, minWidth: 210 }}>Instant Estimate minimum</div>
        <input
          type="number"
          min={0}
          step={1}
          value={minQty}
          placeholder="Company minimum"
          onChange={(e) => setMinQty(e.target.value)}
          style={{ width: 150, border: "1px solid #EBEBEB", borderRadius: 6, padding: "7px 10px", fontFamily: "Poppins", fontSize: 13 }}
        />
        <span style={sub}>yd</span>
        <BasicButton type="button" keepText disabled={busy || minQty === savedMin} onClick={saveMin}>Save</BasicButton>
      </Flex>
    </Box>
  );
};

export default ClientPricingOverrides;
